/**
 * Money-over-time chart card (E2.1 design spec 4.5, round-3 rework 11.5,
 * bar-mode rework): 0 or 1 shop selected -> grouped BARS (gross sales blue /
 * payout yellow / net profit green) for the selected currency, one group per
 * month -- replaces the old LineChart, whose in-progress current month
 * plotted low and read as a decline; 2+ shops selected -> compare mode,
 * unchanged LineChart, one line per shop for a chosen metric (Gross sales |
 * Payout | Net profit). Months with no real data render as gaps
 * (chartHelpers.buildChartSeries / buildShopCompareSeries), never a
 * phantom zero -- the "zero-cliff" the round-3 review flagged. In bar mode,
 * the CURRENT calendar month (when the selected chart year is the current
 * year AND the viewer has revenue:read) gets a second, stacked "shaded"
 * series showing the projected rest-of-month total on top of the actual
 * month-to-date bar (chartHelpers.buildChartProjection) -- gated on
 * revenue:read via the same `canReadRevenue` prop DashboardPage already
 * threads to RevenueCard, so a session without it sees a plain actual bar
 * and no projection caption. Compare mode signals the same "still filling
 * in" state differently: a true hollow/open marker proved impractical with
 * x-charts' LineChart API, so the current month's point is nulled instead
 * (chartHelpers.maskCurrentMonthForCompare) -- see that helper's docstring.
 * The shop filter is populated from the by-shop response already fetched
 * elsewhere on the page. Owner privacy ruling (2026-07-14): a session
 * without shops:read gets ZERO shop-roster exposure, so `shopFilterEnabled`
 * (false for that session) hides the whole ChartFilters control -- shop
 * multi-select AND the metric select together, since compare mode is
 * unreachable without a shop to pick. That session sees only the all-shops
 * aggregate bar chart. Respects prefers-reduced-motion via x-charts'
 * skipAnimation. PERF (spec 11.5): changing the shop/metric selection must
 * refetch ONLY the timeseries call -- DashboardPage isolates that
 * dependency; this component just renders whatever props it's handed, so
 * it has no fetching of its own to leak. Responsive per spec section 8:
 * stays full width on mobile (reduced height, thinned x-axis ticks, legend
 * below); the chart's pixel width is measured from its own container (no
 * fixed px width) so it never forces horizontal viewport scroll.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { TimeseriesMonth, TimeseriesShopEntry } from '../../api/index.ts';
import { useDisplayCurrency } from '../../currency/CurrencyContext.tsx';
import { compactInCurrency, formatInCurrency } from '../../currency/formats.ts';
import { palette } from '../../theme.ts';
import { ChartFilters } from './ChartFilters.tsx';
import {
  CHART_MONTH_LABELS,
  PROJECTION_CAPTION,
  buildChartProjection,
  buildChartSeries,
  buildShopCompareSeries,
  chartColorAt,
  chartHeightPx,
  chartLegendPosition,
  chartTickLabelInterval,
  currentMonthProgressCaption,
  dataAsOfCaption,
  maskCurrentMonthForCompare,
  prefersReducedMotion,
  type ChartMetric,
  type ChartSeries,
  type ShopOption,
} from './chartHelpers.ts';

/** Sensible pre-measurement default -- only matters before the ResizeObserver
 *  (or jsdom, which has none) reports a real width. */
const DEFAULT_CHART_WIDTH = 640;

/** Measures a container's content width via ResizeObserver, falling back to
 *  a fixed default where ResizeObserver is unavailable (jsdom in tests) so
 *  the chart still renders deterministically without a layout engine. */
function useMeasuredWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(DEFAULT_CHART_WIDTH);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.max(200, Math.floor(entry.contentRect.width)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/** Bar-mode metric definitions, in the same order/colours the old 3-series
 *  LineChart used (spec item 1: "same palette order as today"). Keyed off
 *  ChartSeries's own field names so `single[key]` below stays type-safe. */
const BAR_METRICS: ReadonlyArray<{ key: keyof ChartSeries; label: string }> = [
  { key: 'grossSales', label: 'Gross sales' },
  { key: 'payout', label: 'Payout' },
  { key: 'netProfit', label: 'Net profit' },
];

export type { ShopOption };

export interface MoneyChartProps {
  /** The chart's own year (spec 4.5: independent of the KPI/table period). */
  year: number;
  /** Ungrouped 12-month data -- used when 0 or 1 shop is selected. */
  months: TimeseriesMonth[];
  /** group_by=shop data -- used when 2+ shops are selected (compare mode). */
  groupedShops: TimeseriesShopEntry[];
  currencyCode: string | null;
  shopOptions: readonly ShopOption[];
  selectedShopIds: readonly number[];
  onShopIdsChange: (ids: number[]) => void;
  metric: ChartMetric;
  onMetricChange: (metric: ChartMetric) => void;
  /** False for a session without shops:read (owner ruling 2026-07-14) --
   *  hides the shop multi-select and metric select entirely rather than
   *  merely rendering them with an empty shop list. */
  shopFilterEnabled: boolean;
  /** Gates the bar-mode current-month projection series AND its caption --
   *  same revenue:read mechanism DashboardPage already uses to gate
   *  RevenueCard. Without it: a plain actual bar, no projection, no
   *  projection caption. */
  canReadRevenue: boolean;
  /** last_sync_at of every shop in the by-shop response (whatever shape it
   *  came in -- may be empty, e.g. no shops:read), used ONLY to derive the
   *  "Data as of" caption (chartHelpers.dataAsOfCaption); an empty array
   *  means that caption is silently absent. */
  shopSyncTimestamps: readonly (string | null)[];
  compact: boolean;
  loading: boolean;
  error: string | null;
}

export function MoneyChart({
  year,
  months,
  groupedShops,
  currencyCode,
  shopOptions,
  selectedShopIds,
  onShopIdsChange,
  metric,
  onMetricChange,
  shopFilterEnabled,
  canReadRevenue,
  shopSyncTimestamps,
  compact,
  loading,
  error,
}: Readonly<MoneyChartProps>): React.JSX.Element {
  const { convert, displayCurrency } = useDisplayCurrency();
  const [containerRef, width] = useMeasuredWidth();
  const compareMode = selectedShopIds.length >= 2;
  // Real clock, not injectable from a prop -- neither MoneyChart nor its
  // caller ever needed a fake-time seam before this rework; tests that need
  // a fixed "now" use vi.useFakeTimers (same convention DashboardPage.test
  // already established), not a new prop surface.
  const now = new Date();
  const showProjection = !compareMode && canReadRevenue && year === now.getFullYear();

  // PERF (item 3): buildChartSeries/buildShopCompareSeries re-sort and
  // re-derive every month's gap flags from scratch -- memoized on their
  // actual inputs so an unrelated re-render (e.g. the container's
  // ResizeObserver width tick) doesn't redo that work every time.
  const nativeSingle = useMemo(
    () => (!compareMode && currencyCode ? buildChartSeries(months, currencyCode, year) : { grossSales: [], payout: [], netProfit: [] }),
    [compareMode, currencyCode, months, year],
  );
  const chartNativeCode = currencyCode ?? 'PGK';
  const conversionAvailable = convert(0, currencyCode) !== null;
  const chartDisplayCode = conversionAvailable ? displayCurrency : chartNativeCode;
  const single = useMemo(() => {
    const mapValues = (values: (number | null)[]): (number | null)[] =>
      values.map((value) => value === null ? null : (convert(value, currencyCode) ?? value));
    return {
      grossSales: mapValues(nativeSingle.grossSales),
      payout: mapValues(nativeSingle.payout),
      netProfit: mapValues(nativeSingle.netProfit),
    };
  }, [convert, currencyCode, nativeSingle]);
  const projection = useMemo(() => buildChartProjection(single, year), [single, year]);
  // The projection math can suppress ITSELF (all-null arrays: before day 8,
  // or a zero/negative MTD) -- without this, the "(projected)" legend
  // entries and the shaded-caption line would still render with nothing
  // actually shaded.
  const projectionHasValues = [...projection.grossSales, ...projection.payout, ...projection.netProfit].some((v) => v !== null);
  const projectionVisible = showProjection && projectionHasValues;
  const nativeCompareSeries = useMemo(
    () => (compareMode && currencyCode ? buildShopCompareSeries(groupedShops, selectedShopIds, currencyCode, metric, year) : []),
    [compareMode, currencyCode, groupedShops, selectedShopIds, metric, year],
  );
  const compareSeries = useMemo(
    () => nativeCompareSeries.map((series) => ({
      ...series,
      data: series.data.map((value) => value === null ? null : (convert(value, currencyCode) ?? value)),
    })),
    [convert, currencyCode, nativeCompareSeries],
  );
  const maskedCompareSeries = useMemo(() => maskCurrentMonthForCompare(compareSeries, year), [compareSeries, year]);

  // Bar mode: one "actual" series per metric, plus (only when the
  // projection is being shown at all) a second, stacked "projected
  // remainder" series in the same hue at reduced opacity -- x-charts stacks
  // series sharing a `stack` id, one id per metric (spec item 2).
  const barSeries = BAR_METRICS.flatMap(({ key, label }, i) => {
    const color = chartColorAt(i);
    const valueFormatter = (value: number | null): string => value === null ? '' : formatInCurrency(value, chartDisplayCode);
    if (!projectionVisible) return [{ data: single[key], label, color, valueFormatter }];
    return [
      { data: single[key], label, color, stack: key, valueFormatter },
      {
        data: projection[key],
        label: `${label} (projected)`,
        color: `color-mix(in srgb, ${color} 40%, transparent)`,
        stack: key,
        valueFormatter,
      },
    ];
  });

  const lineSeries = maskedCompareSeries.map((s, i) => ({
    data: s.data,
    label: s.label,
    color: chartColorAt(i),
    showMark: false,
    connectNulls: false,
    valueFormatter: (value: number | null): string => value === null ? '' : formatInCurrency(value, chartDisplayCode),
  }));

  const hasSeriesData = compareMode ? compareSeries.length > 0 : currencyCode !== null;

  // Captions (spec item 4): current-month-in-progress, only-when-projected,
  // and data-freshness lines, each independently gated and rendered only
  // when applicable -- an empty array means no caption block at all.
  const captionLines = [currentMonthProgressCaption(year), projectionVisible ? PROJECTION_CAPTION : null, dataAsOfCaption(shopSyncTimestamps)].filter(
    (c): c is string => c !== null,
  );

  return (
    <Paper sx={{ p: 2.5, width: '100%', minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
        <Typography variant="h6">Money over time</Typography>
        {shopFilterEnabled && (
          <ChartFilters
            shopOptions={shopOptions}
            selectedShopIds={selectedShopIds}
            onShopIdsChange={onShopIdsChange}
            metric={metric}
            onMetricChange={onMetricChange}
            compareMode={compareMode}
          />
        )}
      </Box>

      {error && (
        <Alert role="alert" severity="error">
          {error}
        </Alert>
      )}

      {!error && (
        <Box ref={containerRef} sx={{ width: '100%', minWidth: 0 }}>
          {loading && (
            // Owner ruling (2026-07-15, "the skeleton looks weird"): a
            // spinner centered both ways inside a box that still reserves
            // the exact chart height, so the swap-in causes zero layout
            // shift without a skeleton.
            <Box
              data-testid="money-chart-loading"
              sx={{ height: chartHeightPx(compact), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}
            >
              <CircularProgress size={24} />
              <Typography sx={{ color: palette.textSecondary }}>Loading ...</Typography>
            </Box>
          )}
          {!loading && !hasSeriesData && <Typography sx={{ color: palette.textSecondary, py: 4 }}>No reports yet.</Typography>}
          {!loading && hasSeriesData && !compareMode && (
            <BarChart
              width={width}
              height={chartHeightPx(compact)}
              skipAnimation={prefersReducedMotion()}
              xAxis={[{ scaleType: 'band', data: CHART_MONTH_LABELS, tickLabelInterval: chartTickLabelInterval(compact) }]}
              yAxis={[{ valueFormatter: (v: number) => compactInCurrency(v, chartDisplayCode) }]}
              series={barSeries}
              slotProps={{ legend: { position: chartLegendPosition(compact) } }}
              margin={{ left: 56, right: 16, top: 16, bottom: compact ? 48 : 32 }}
            />
          )}
          {!loading && hasSeriesData && compareMode && (
            <LineChart
              width={width}
              height={chartHeightPx(compact)}
              skipAnimation={prefersReducedMotion()}
              xAxis={[{ scaleType: 'point', data: CHART_MONTH_LABELS, tickLabelInterval: chartTickLabelInterval(compact) }]}
              yAxis={[{ valueFormatter: (v: number) => compactInCurrency(v, chartDisplayCode) }]}
              series={lineSeries}
              slotProps={{ legend: { position: chartLegendPosition(compact) } }}
              margin={{ left: 56, right: 16, top: 16, bottom: compact ? 48 : 32 }}
            />
          )}
          {!loading && hasSeriesData && captionLines.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {captionLines.map((line) => (
                <Typography key={line} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {line}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default MoneyChart;
